/**
 * Lead submission ; Layer 4 + Layer 9.
 *
 * - Optimistic success state within 200ms (caller flips UI immediately).
 * - 1 automatic retry after ~2s on transport failure.
 * - Offline queue: failed submits land in sessionStorage and replay on `online`.
 * - Idempotent: uses event_id; the server returns the same lead_id on duplicates.
 */
import { getAttribution, newEventId, trackEvent, readFbCookies } from "./tracking";
import { track, getVisitorId } from "./eventBus";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const QUEUE_KEY = "zenith.lead_queue";

function readQueue() {
  try {
    return JSON.parse(sessionStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}
function writeQueue(q) {
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* ignore */
  }
}

// Webview-safe request timeout. AbortSignal.timeout() throws TypeError on older
// iOS Safari and Facebook/Instagram in-app webviews - a large share of paid
// traffic - so we use AbortController + setTimeout, which works everywhere.
function timeoutSignal(ms) {
  const c = new AbortController();
  const t = setTimeout(() => c.abort(), ms);
  return { signal: c.signal, clear: () => clearTimeout(t) };
}

async function postOnce(payload) {
  // A hung network never rejects on its own; without this the retry/offline-queue
  // safety net below never engages and the visitor sits on "Submitting…" forever.
  const to = timeoutSignal(15000);
  let res;
  try {
    res = await fetch(`${API}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: to.signal,
    });
  } finally {
    to.clear();
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`http ${res.status}: ${text.slice(0, 200)}`);
    err.status = res.status;
    // Attach structured detail for 422 validation errors (field-level feedback)
    if (res.status === 422) {
      try {
        const body = JSON.parse(text);
        if (body && body.detail && typeof body.detail === "object") {
          err.detail = body.detail;
        }
      } catch {
        /* non-JSON body - no detail */
      }
    }
    throw err;
  }
  return res.json();
}

/**
 * Submit a lead with retry + offline queue.
 * @param {Object} opts
 * @param {"zenith"|"mortgages"|"realtor"} opts.source
 * @param {string} opts.formName
 * @param {Object} opts.fields  All form answers
 * @param {string} [opts.name]
 * @param {string} [opts.email]
 * @param {string} [opts.phone]
 * @param {string} [opts.city]
 * @param {string} [opts.honeypot] honeypot field value (must be empty)
 * @param {Object} [opts.consent]
 */
export async function submitLead(opts) {
  const event_id = newEventId();
  const metaEventName = opts.metaEventName || "Lead";
  const busEventName = opts.busEventName || "lead_submit";
  // Read Meta attribution cookies at submit time - most reliable moment
  // (pixel has been loaded and running for the full session).
  const { fbc, fbp } = readFbCookies();
  const payload = {
    event_id,
    source: opts.source,
    form_name: opts.formName,
    fields: opts.fields || {},
    name: opts.name || null,
    email: opts.email || null,
    phone: opts.phone || null,
    city: opts.city || null,
    company_website: opts.honeypot || "",
    fbc,   // _fbc cookie → CAPI match quality (links event to ad click)
    fbp,   // _fbp cookie → CAPI match quality (browser identity)
    click_attribution: getAttribution(),
    visitor_id: getVisitorId(),   // hashed server-side into Meta external_id (same key as the PageView relay)
    consent: opts.consent || null,
    user_agent_hint:
      typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
    page_url: typeof window !== "undefined" ? window.location.href : "",
  };

  // Browser conversion legs. These fire ONLY once the server has accepted the lead (or
  // queued it for replay): firing before the POST meant a lead the server rejected
  // (422: bad email MX, phone, rate limit) still counted as a Meta/DomainEx conversion,
  // so Meta trained on junk and reported ~2x the real lead count. Same event_id on every
  // leg, so Meta still dedupes against the server CAPI event.
  // purchaseValue (buyer-readiness score) enables value-based bidding on all legs.
  const fireConversion = () => {
    // Never let a tracking failure read as a failed POST (the caller's catch retries/queues).
    try {
      fireConversionLegs();
    } catch {
      /* non-blocking */
    }
  };
  const fireConversionLegs = () => {
    const pixelProps = { content_name: opts.formName, source: opts.source };
    if (opts.purchaseValue != null) {
      pixelProps.value = opts.purchaseValue;
      pixelProps.currency = "CAD";
    }
    trackEvent(metaEventName, pixelProps, event_id);
    track(busEventName, {
      form: opts.formName,
      source: opts.source,
      has_email: !!opts.email,
      has_phone: !!opts.phone,
      has_city: !!opts.city,
      lead_event_id: event_id,
    });

    // DomainEx browser leg - mirrors the Meta event exactly: same event name
    // (Purchase -> "purchase", never "lead", which DomainEx would forward to Meta
    // as a second, un-deduped Lead conversion), same event_id, same native
    // value/currency. The server postback (backend _domainex_postback) is the leg
    // that carries hashed match keys; this one stays PII-free by design.
    // Guarded so an ad-blocked or slow-loading pixel never breaks submission.
    try {
      if (typeof window !== "undefined" && window.DomainEx) {
        const dxProps = { event_id, form: opts.formName, source: opts.source };
        if (pixelProps.value != null) {
          dxProps.value = pixelProps.value;
          dxProps.currency = pixelProps.currency;
        }
        window.DomainEx.track(metaEventName === "Purchase" ? "purchase" : "lead", dxProps);
      }
    } catch {
      /* non-blocking */
    }
  };

  try {
    const accepted = await postOnce(payload);
    fireConversion();
    return accepted;
  } catch (err1) {
    // 422 = server-side validation rejection - surface immediately, no retry/queue
    if (err1.status === 422) throw err1;
    // Transport or server error - wait ~2s and retry once
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const accepted = await postOnce(payload);
      fireConversion();
      return accepted;
    } catch (err2) {
      if (err2.status === 422) throw err2;
      // Queue for replay on `online` - resolve so the form shows success to the visitor
      const q = readQueue();
      q.push({ ...payload, _queuedAt: Date.now() });
      writeQueue(q);
      fireConversion(); // visitor sees success and the server accepts it on replay
      return { lead_id: payload.event_id, event_id: payload.event_id, queued: true };
    }
  }
}

/**
 * Partial-lead capture: the visitor gave an email but hasn't finished. The
 * backend holds it and, if they never submit, sends one check-in email from
 * Dorian. Deliberately NOT a conversion - no Meta/DomainEx event fires here.
 * Fire-and-forget; keepalive so it survives the tab closing right after.
 */
export function capturePartial({ partialId, source, formName, email, name, fields, honeypot }) {
  try {
    fetch(`${API}/leads/partial`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        partial_id: partialId,
        source,
        form_name: formName,
        email,
        name: name || null,
        fields: fields || {},
        company_website: honeypot || "",
        click_attribution: getAttribution(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
      }),
    }).catch(() => { /* non-blocking */ });
  } catch {
    /* non-blocking */
  }
}

export function installOfflineReplay() {
  if (typeof window === "undefined") return;
  const replay = async () => {
    const q = readQueue();
    if (!q.length) return;
    const remaining = [];
    for (const item of q) {
      try {
        await postOnce(item);
      } catch {
        remaining.push(item);
      }
    }
    writeQueue(remaining);
  };
  window.addEventListener("online", replay);
  // Also try once on load in case the visit started offline
  if (navigator.onLine) replay();
}

export async function logConsent({ accepted, version, excerpt, granular }) {
  try {
    await fetch(`${API}/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accepted,
        text_version: version,
        text_excerpt: excerpt,
        granular: granular || null,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      }),
    });
  } catch {
    /* non-blocking */
  }
}
